/**
 * @fileoverview Shared helpers to transform external anchors in HTML strings.
 */

const ANCHOR_TAG_REGEX = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
const HREF_ATTR_REGEX = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;
const REL_ATTR_REGEX = /\brel\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

interface TransformExternalAnchorsOptions {
  /**
   * Adds `rel="nofollow"` to external anchors that remain clickable.
   */
  addNoFollow?: boolean;
  /**
   * Replaces external anchors with their inner HTML, removing clickability.
   */
  convertToPlainText?: boolean;
}

/**
 * Extracts the href value from an anchor tag attribute string.
 *
 * @param attributes
 *   The raw attribute string found inside an `<a ...>` element.
 *
 * @returns
 *   The href value when present, otherwise null.
 */
const getHref = (attributes: string): string | null => {
  const match = attributes.match(HREF_ATTR_REGEX);

  if (!match) {
    return null;
  }

  const hrefDoubleQuoted = match[1];
  const hrefSingleQuoted = match[2];
  const hrefUnquoted = match[3];

  return hrefDoubleQuoted ?? hrefSingleQuoted ?? hrefUnquoted ?? null;
};

/**
 * Normalizes a full site URL down to its origin.
 *
 * This keeps comparisons stable even when the configured client URL includes
 * a base path such as `https://example.com/jobs`.
 *
 * @param siteUrl
 *   A full site URL, usually taken from `CLIENT_URL` or runtime host context.
 *
 * @returns
 *   The normalized origin, or null when the input cannot be parsed.
 */
export const normalizeSiteOrigin = (siteUrl?: string | null): string | null => {
  if (!siteUrl) {
    return null;
  }

  try {
    return new URL(siteUrl).origin;
  } catch {
    return null;
  }
};

/**
 * Checks whether an href points to an external HTTP(S) origin.
 *
 * Relative URLs, invalid URLs, and non-HTTP(S) schemes are treated as
 * non-external so they are left untouched by callers.
 *
 * @param href
 *   The href value extracted from an anchor.
 * @param siteOrigin
 *   The normalized origin of the current site.
 *
 * @returns
 *   True when the href is an absolute HTTP(S) URL on another origin.
 */
const isExternalHttpLink = (
  href: string | null,
  siteOrigin?: string | null,
): boolean => {
  if (!href || !siteOrigin) {
    return false;
  }

  try {
    const url = new URL(href);

    if (!['http:', 'https:'].includes(url.protocol)) {
      return false;
    }

    return url.origin !== siteOrigin;
  } catch {
    return false;
  }
};

/**
 * Ensures an anchor attribute string contains `rel="nofollow"`.
 *
 * @param attributes
 *   The raw attribute string found inside an `<a ...>` element.
 *
 * @returns
 *   The updated attribute string with `nofollow` appended when needed.
 */
const addNoFollowRel = (attributes: string): string => {
  const relMatch = attributes.match(REL_ATTR_REGEX);

  if (!relMatch) {
    return `${attributes} rel="nofollow"`;
  }

  const currentRelValue = relMatch[1] ?? relMatch[2] ?? '';
  const relValues = currentRelValue
    .split(/\s+/)
    .map((value) => value.trim())
    .filter(Boolean);

  if (relValues.includes('nofollow')) {
    return attributes;
  }

  const nextRelValue = `${currentRelValue} nofollow`.trim();

  return attributes.replace(REL_ATTR_REGEX, `rel="${nextRelValue}"`);
};

/**
 * Applies the configured transformation to one external anchor match.
 *
 * @param fullMatch
 *   The original full anchor HTML.
 * @param attributes
 *   The raw attribute string from the anchor.
 * @param innerContent
 *   The inner HTML contained by the anchor.
 * @param siteOrigin
 *   The normalized current-site origin used for external link detection.
 * @param options
 *   Controls whether external anchors become plain text or get `nofollow`.
 *
 * @returns
 *   The transformed HTML for this anchor match.
 */
const transformAnchorMatch = (
  fullMatch: string,
  attributes: string,
  innerContent: string,
  siteOrigin: string,
  options: TransformExternalAnchorsOptions,
): string => {
  const href = getHref(attributes);

  if (!isExternalHttpLink(href, siteOrigin)) {
    return fullMatch;
  }

  if (options.convertToPlainText) {
    return innerContent;
  }

  if (options.addNoFollow) {
    return `<a${addNoFollowRel(attributes)}>${innerContent}</a>`;
  }

  return fullMatch;
};

/**
 * Transforms external anchors in an HTML string.
 *
 * The helper only affects absolute external HTTP(S) links. Relative links,
 * same-origin links, invalid hrefs, and non-HTTP(S) links are returned
 * unchanged.
 *
 * @param html
 *   The HTML string to inspect.
 * @param siteOrigin
 *   The normalized current-site origin used to identify external links.
 * @param options
 *   Controls whether external anchors should be unwrapped or receive
 *   `rel="nofollow"`.
 *
 * @returns
 *   The updated HTML string.
 */
export const transformExternalAnchors = (
  html: string,
  siteOrigin?: string | null,
  options: TransformExternalAnchorsOptions = {},
): string => {
  const { addNoFollow = false, convertToPlainText = false } = options;

  if (!html || !siteOrigin || (!addNoFollow && !convertToPlainText)) {
    return html;
  }

  return html.replace(
    ANCHOR_TAG_REGEX,
    (fullMatch, attributes, innerContent: string) =>
      transformAnchorMatch(fullMatch, attributes, innerContent, siteOrigin, {
        addNoFollow,
        convertToPlainText,
      }),
  );
};
